import { Text, View, TouchableOpacity, RefreshControl, ScrollView } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import CutCard from '@/components/General/CutCard';
import SolidButton from '@/components/General/SolidButton';
import SearchBox from '@/components/General/inputs/SearchBox';
import { deletePath, duplicatePath } from '@/data/paths';
import { useRouter, useNavigation } from 'expo-router';
import { showAlert } from '@/utils/showAlert';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import { groupDashboardAutomations } from '@/utils/automationDashboard';
import { TRIGGER_TYPE_META } from '@/utils/automationBuilder';

const FilterChip = ({ label, selected, onPress, testID, icon }) => (
    <TouchableOpacity testID={testID} accessibilityRole="button" accessibilityState={{ selected }} aria-pressed={selected} onPress={onPress} style={styles.filterChip(selected)}>
        {icon && <Unicon name={icon} size={15} colorVariant={selected ? 'paths' : 'textSecondary'} />}
        <Text style={styles.filterChipText(selected)}>{label}</Text>
    </TouchableOpacity>
);

const PathCard = ({ path, router, loggedInUser, onDelete, onDuplicate }) => {
    const menuOptions = useMemo(() => {
        const opts = [];
        if (loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) {
            opts.push({
                name: "Edit Automation",
                icon: dropdownIcons.edit,
                onSelect: () => router.push({ pathname: "/automations/create", params: { id: path.id } }),
            });
        }
        // Duplicate only for v2 — a copy of a legacy definition would create a
        // new legacy automation, and legacy creation is retired.
        if (path.schemaVersion === 2 && (loggedInUser?.can?.("Create Paths") || loggedInUser?.isAdmin)) {
            opts.push({
                name: "Duplicate Automation",
                icon: dropdownIcons.copy,
                onSelect: () => onDuplicate(path.id),
            });
        }
        if (loggedInUser?.can?.("Delete Paths") || loggedInUser?.isAdmin) {
            opts.push({
                name: "Delete Automation",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => {
                    showAlert({
                        title: "Delete Automation",
                        subtitle: `Are you sure you want to delete "${path.name}"? This will also remove all steps and run data. This cannot be undone.`,
                        isDestructive: true,
                        confirmText: "Delete",
                        onConfirm: () => onDelete(path.id),
                    });
                },
            });
        }
        return opts;
    }, [path.id, path.name, path.schemaVersion, loggedInUser, router, onDelete, onDuplicate]);

    return (
        <CutCard style={styles.card} accentColor="paths" testID={`automation-card-${path.id}`}>
            <View style={styles.cardBody}>
                <View style={styles.cardIconContainer}>
                    <Unicon name={path.schemaVersion === 2 ? 'git-network-outline' : 'archive-outline'} size={20} colorVariant="paths" />
                </View>
                <TouchableOpacity
                    style={styles.cardContent}
                    onPress={() => router.push("/automations/" + path.id)}
                    activeOpacity={0.7}
                    testID={`automation-open-${path.id}`}
                    accessibilityRole="button"
                    accessibilityLabel={`Open ${path.name}`}
                >
                    <Text numberOfLines={1} style={styles.cardTitle}>{path.name}</Text>
                    {!!path.description && <Text numberOfLines={2} style={styles.cardDescription}>{path.description}</Text>}
                </TouchableOpacity>
                {menuOptions.length > 0 && <DropdownMenu testID={`automation-menu-${path.id}`} options={menuOptions} />}
            </View>

            <View style={styles.cardMeta}>
                <View style={styles.metaChip(path.isActive ? "success" : "textSecondary")}>
                    <Unicon name={path.isActive ? "radio-button-on" : "radio-button-off"} size={12} colorVariant={path.isActive ? "success" : "textSecondary"} />
                    <Text style={styles.metaChipText(path.isActive ? "success" : "textSecondary")}>{path.isActive ? "Enabled" : "Disabled"}</Text>
                </View>

                <View style={styles.metaChipNeutral}>
                    <Unicon name="list-outline" size={12} colorVariant="textSecondary" />
                    <Text style={styles.metaChipNeutralText}>{path.stepCount || 0} step{path.stepCount !== 1 ? 's' : ''}</Text>
                </View>

                {path.activeEnrollmentCount > 0 && (
                    <View style={styles.metaChip("paths")}>
                        <Unicon name="play-outline" size={12} colorVariant="paths" />
                        <Text style={styles.metaChipText("paths")}>{path.activeEnrollmentCount} running</Text>
                    </View>
                )}

                {path.schemaVersion !== 2 && (
                    <View style={styles.metaChipNeutral}>
                        <Unicon name="archive-outline" size={12} colorVariant="textSecondary" />
                        <Text style={styles.metaChipNeutralText}>Legacy</Text>
                    </View>
                )}
            </View>
        </CutCard>
    );
};

export default function AutomationsDashboardScreen() {
    const [searchText, setSearchText] = useState("");
    const [legacy, setLegacy] = useState(false);
    const [showDisabled, setShowDisabled] = useState(false);
    const [collapsed, setCollapsed] = useState({});

    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: paths = [], isLoading, isFetching, isError } = Query.paths.all({ options: { refetchOnMount: 'always' } });
    const canCreate = loggedInUser?.can?.("Create Paths") || loggedInUser?.isAdmin;

    const deletePathMutation = useMutation({
        mutationFn: (pathID) => deletePath({ pathID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
        },
    });

    const duplicatePathMutation = useMutation({
        mutationFn: (pathID) => duplicatePath({ pathID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
        },
    });

    useEffect(() => {
        navigation.setOptions({
            headerRight: () =>
                canCreate && (
                    <DropdownMenu
                        options={[
                            { name: "New Automation", icon: dropdownIcons.add, onSelect: () => router.push("/automations/new"), testID: "create-automation-v2" },
                        ]}
                    />
                ),
        });
    }, [navigation, canCreate, router]);

    const groups = useMemo(() => groupDashboardAutomations(paths, { legacy, showDisabled, search: searchText }), [paths, legacy, showDisabled, searchText]);
    const currentPaths = paths.filter(path => (path.schemaVersion !== 2) === legacy);
    const enabledCount = currentPaths.filter(path => path.isActive).length;
    const disabledCount = currentPaths.length - enabledCount;
    const runningCount = currentPaths.reduce((total, path) => total + (path.activeEnrollmentCount || 0), 0);
    const visibleCount = new Set(groups.flatMap(group => group.paths.map(path => path.id))).size;
    const modernCount = paths.filter(path => path.schemaVersion === 2).length;
    const legacyCount = paths.length - modernCount;

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
    }, [queryClient]);

    const handleDelete = useCallback((pathID) => {
        deletePathMutation.mutate(pathID);
    }, [deletePathMutation]);

    const handleDuplicate = useCallback((pathID) => {
        duplicatePathMutation.mutate(pathID);
    }, [duplicatePathMutation]);

    const toggleGroup = (key) => setCollapsed(previous => ({ ...previous, [key]: !previous[key] }));

    return (
        <ScrollView
            testID="automations-dashboard"
            style={styles.container}
            contentContainerStyle={styles.content}
            contentInsetAdjustmentBehavior="automatic"
            keyboardShouldPersistTaps="handled"
            refreshControl={<RefreshControl refreshing={!isLoading && isFetching} onRefresh={refreshList} />}
        >
            <View style={styles.hero}>
                <View style={styles.heroCopy}>
                    <Text style={styles.eyebrow}>TRIGGERS & WORKFLOWS</Text>
                    <Text style={styles.heroTitle}>Automations</Text>
                    <Text style={styles.heroText}>Keep work moving. Find each workflow by what starts it.</Text>
                </View>
                {canCreate && <SolidButton testID="automations-dashboard-create" text="New automation" iconName="add" iconBefore type="paths" onPress={() => router.push('/automations/new')} />}
            </View>

            <View style={styles.viewControls}>
                <FilterChip label={`Automations (${modernCount})`} selected={!legacy} onPress={() => setLegacy(false)} testID="automations-view-current" icon="git-network-outline" />
                <FilterChip label={`Legacy (${legacyCount})`} selected={legacy} onPress={() => setLegacy(true)} testID="automations-view-legacy" icon="archive-outline" />
            </View>

            <CutCard style={styles.overview} accentColor="paths" contentStyle={styles.overviewContent} testID="automations-overview">
                <View style={styles.overviewHeading}>
                    <Text style={styles.eyebrow}>{legacy ? 'LEGACY WORKFLOWS' : 'AT A GLANCE'}</Text>
                    <Text style={styles.overviewTitle}>{legacy ? 'Legacy automations' : 'Your workflows'}</Text>
                </View>
                <View style={styles.stats}>
                    {[
                        { value: enabledCount, label: 'enabled', icon: 'radio-button-on', tone: 'success' },
                        { value: disabledCount, label: 'disabled', icon: 'pause-circle-outline', tone: 'textSecondary' },
                        { value: runningCount, label: 'active runs', icon: 'play-outline', tone: 'paths' },
                    ].map(stat => <View key={stat.label} style={styles.stat}>
                        <Unicon name={stat.icon} size={18} colorVariant={stat.tone} />
                        <Text style={styles.statValue}>{stat.value}</Text>
                        <Text style={styles.statLabel}>{stat.label}</Text>
                    </View>)}
                </View>
            </CutCard>

            {legacy && <View style={styles.legacyNotice} testID="automations-legacy-notice">
                <Unicon name="archive-outline" size={18} colorVariant="textSecondary" />
                <Text style={styles.legacyNoticeText}>Built with the original builder. Legacy automations stay separate and continue working as configured.</Text>
            </View>}

            <View style={styles.toolbar}>
                <View style={styles.searchContainer}><SearchBox testID="automations-dashboard" searchText={searchText} onChangeText={setSearchText} placeholder="Search names, descriptions, or triggers..." /></View>
                <FilterChip label="Show disabled" selected={showDisabled} onPress={() => setShowDisabled(value => !value)} testID="automations-show-disabled" icon={showDisabled ? 'checkbox-outline' : 'square-outline'} />
            </View>

            {isLoading ? <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View> : isError ? (
                <CutCard style={styles.emptyCard} contentStyle={styles.emptyContent}>
                    <Text style={styles.emptyTitle}>Automations could not load</Text>
                    <Text style={styles.emptyText}>Try refreshing to load your workflows.</Text>
                    <SolidButton text="Try again" type="paths" onPress={refreshList} testID="automations-retry" />
                </CutCard>
            ) : <>
                <View style={styles.resultsHeader}>
                    <Text style={styles.sectionTitle}>By trigger</Text>
                    <Text style={styles.resultCount} testID="automations-result-count">{visibleCount} automation{visibleCount !== 1 ? 's' : ''} / {groups.length} trigger{groups.length !== 1 ? 's' : ''}</Text>
                </View>
                {groups.map(group => (
                    <View key={group.key} testID={`automation-group-${group.key}`} style={styles.group}>
                        <TouchableOpacity
                            style={styles.groupHeader}
                            testID={`automation-group-toggle-${group.key}`}
                            accessibilityRole="button"
                            accessibilityLabel={`${group.label}, ${group.paths.length} automation${group.paths.length !== 1 ? 's' : ''}`}
                            accessibilityState={{ expanded: !collapsed[group.key] }}
                            aria-expanded={!collapsed[group.key]}
                            onPress={() => toggleGroup(group.key)}
                        >
                            <Unicon name={collapsed[group.key] ? 'chevron-forward' : 'chevron-down'} size={17} colorVariant="paths" />
                            <View style={styles.groupHeading}>
                                <Text style={styles.groupTitle} testID="automation-trigger-label">{group.label}</Text>
                                {!!TRIGGER_TYPE_META[group.key]?.description && <Text style={styles.groupDescription}>{TRIGGER_TYPE_META[group.key].description}</Text>}
                            </View>
                            <View style={styles.countBadge}><Text style={styles.countText}>{group.paths.length}</Text></View>
                        </TouchableOpacity>
                        {!collapsed[group.key] && <View style={styles.cards}>
                            {group.paths.map(path => <View key={path.id} style={styles.cardSlot}>
                                <PathCard path={path} router={router} loggedInUser={loggedInUser} onDelete={handleDelete} onDuplicate={handleDuplicate} />
                            </View>)}
                        </View>}
                    </View>
                ))}
                {groups.length === 0 && <CutCard style={styles.emptyCard} contentStyle={styles.emptyContent} testID="automations-empty">
                    <Unicon name={legacy ? 'archive-outline' : 'git-network-outline'} size={28} colorVariant="paths" />
                    <Text style={styles.emptyTitle}>{searchText.trim() ? 'No matching automations' : currentPaths.length ? 'No enabled automations' : legacy ? 'No legacy automations' : 'Create your first automation'}</Text>
                    <Text style={styles.emptyText}>{searchText.trim() ? 'Try another name, description, or trigger.' : currentPaths.length ? 'Show disabled workflows to find and enable one.' : legacy ? 'Automations built with the original builder will appear here.' : 'Choose a trigger, then build the steps that follow.'}</Text>
                    {currentPaths.length > 0 && !showDisabled && <SolidButton text="Show disabled" type="paths" variant="muted" onPress={() => setShowDisabled(true)} testID="automations-empty-show-disabled" />}
                    {!currentPaths.length && !legacy && canCreate && <SolidButton text="New automation" type="paths" onPress={() => router.push('/automations/new')} testID="automations-empty-create" />}
                </CutCard>}
            </>}
        </ScrollView>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1 },
    content: { width: '100%', maxWidth: 1240, alignSelf: 'center', paddingHorizontal: 20, paddingTop: 22, paddingBottom: 110, gap: 20 },
    hero: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
    heroCopy: { flex: 1, minWidth: 230 },
    eyebrow: { color: theme.colors.paths, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.3 },
    heroTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 30, lineHeight: 38, marginTop: 4 },
    heroText: { color: theme.colors.textSecondary, fontFamily: theme.fonts.body, fontSize: 14, lineHeight: 21, marginTop: 4 },
    viewControls: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    filterChip: (selected) => ({
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, minHeight: 44,
        paddingHorizontal: 13, paddingVertical: 10, borderRadius: 8,
        backgroundColor: selected ? theme.colors.pathsContainer : theme.colors.surface,
        _web: { _hover: { backgroundColor: theme.colors.hover }, _focusVisible: { outlineColor: theme.colors.paths, outlineWidth: 2, outlineStyle: 'solid', outlineOffset: 3 } },
    }),
    filterChipText: (selected) => ({ color: selected ? theme.colors.paths : theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 12 }),
    overview: { boxShadow: theme.shadows.card },
    overviewContent: { padding: 22, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 22 },
    overviewHeading: { flexGrow: 1, gap: 5 },
    overviewTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 19 },
    stats: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 22 },
    stat: { flexDirection: 'row', alignItems: 'center', gap: 7 },
    statValue: { color: theme.colors.text, fontFamily: theme.fonts.bold, fontSize: 21, fontVariant: ['tabular-nums'] },
    statLabel: { color: theme.colors.textSecondary, fontFamily: theme.fonts.body, fontSize: 12 },
    legacyNotice: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 4 },
    legacyNoticeText: { flex: 1, color: theme.colors.textSecondary, fontSize: 13, lineHeight: 19 },
    toolbar: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
    searchContainer: { flexGrow: 1, flexBasis: 280 },
    resultsHeader: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    sectionTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 20 },
    resultCount: { color: theme.colors.textSecondary, fontSize: 12 },
    group: { gap: 12 },
    groupHeader: {
        flexDirection: 'row', alignItems: 'center', minHeight: 48, gap: 10, paddingVertical: 4, paddingHorizontal: 2,
        _web: { _focusVisible: { outlineColor: theme.colors.paths, outlineWidth: 2, outlineStyle: 'solid', outlineOffset: 3 } },
    },
    groupHeading: { flex: 1, gap: 3 },
    groupTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 16 },
    groupDescription: { color: theme.colors.textSecondary, fontSize: 12, lineHeight: 18 },
    countBadge: { minWidth: 28, height: 28, paddingHorizontal: 8, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.pathsContainer },
    countText: { color: theme.colors.paths, fontFamily: theme.fonts.bold, fontSize: 12, fontVariant: ['tabular-nums'] },
    cards: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
    cardSlot: { flexGrow: 1, flexBasis: 340, minWidth: 0, maxWidth: '100%' },
    emptyCard: { boxShadow: theme.shadows.card },
    emptyContent: { alignItems: 'center', padding: 28, gap: 12 },
    emptyTitle: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 18, textAlign: 'center' },
    emptyText: { color: theme.colors.textSecondary, fontSize: 13, lineHeight: 20, textAlign: 'center', maxWidth: 440 },
    loadingContainer: { padding: 40, alignItems: "center", justifyContent: "center" },

    card: {
        flex: 1,
        boxShadow: theme.shadows.card,
    },
    cardBody: {
        flexDirection: "row",
        alignItems: "center",
        padding: 18,
        paddingRight: 24,
        gap: 12,
    },
    cardIconContainer: {
        width: 40,
        height: 40,
        borderRadius: 8,
        backgroundColor: theme.colors.pathsContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    cardContent: {
        flex: 1, minWidth: 0, minHeight: 44, justifyContent: 'center',
        _web: { _focusVisible: { outlineColor: theme.colors.paths, outlineWidth: 2, outlineStyle: 'solid', outlineOffset: 3 } },
    },
    cardTitle: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    cardDescription: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        marginTop: 2,
        lineHeight: 18,
    },
    cardMeta: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        paddingHorizontal: 24,
        paddingBottom: 18,
        paddingTop: 0,
    },
    metaChip: (colorVariant) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        backgroundColor: theme.colors[colorVariant + "Container"] || theme.colors.surfaceVariant,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    }),
    metaChipText: (colorVariant) => ({
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.textSecondary,
    }),
    metaChipNeutral: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        backgroundColor: theme.colors.surfaceVariant,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    metaChipNeutralText: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
    },
}));
